import { RpcError } from "grpc-web";
import { useState } from "react";
import { getUsers } from "../utils/api/auth";
import { dummyPaginatedApiResponse } from "../utils/consts";
import { PaginatedApiResponse, PaginationResponse } from "../utils/types";
import { GetUsersResponse, User as ApiUser, UserData } from "../utils/api/protos/messages/auth.message_pb";
import useClaimsToast from "./useToast";

// Import constants from users constants file
import { STATUS_OPTIONS, USERS_TABS } from "../pages/users/constants";
import { convertUnixTimestampToLocalDate } from "../utils/helpers";

export type UserTabKey = (typeof USERS_TABS)[keyof typeof USERS_TABS];

// Define filter state interface
interface UserFilters {
	page: number;
	pageSize: number;
	sortBy: string;
	sortOrder: string;
	emailFilter?: string;
	nameFilter?: string;
	externalIdFilter?: string;
	rolesList?: string[];
	status?: string;
}

/**
 * Maps user data from API response to table format
 */
const mapUserToTableData = (user: UserData) => ({
	id: user.getId(),
	externalId: user.getExternalId(),
	email: user.getEmail(),
	username: user.getUsername(),
	name: user.getName(),
	roles: user.getRole().replaceAll(",", ", "),
	status: STATUS_OPTIONS.find((item) => item.value === user.getStatus())?.label,
	createdAt: convertUnixTimestampToLocalDate(user.getCreatedAt()).toLocaleString(),
	confirmedAt: user.getConfirmedAt()
		? convertUnixTimestampToLocalDate(user.getConfirmedAt()).toLocaleString()
		: undefined
});

/**
 * Maps API response to pagination data
 */
const mapPaginationData = (response: GetUsersResponse): PaginationResponse => ({
	current_page: response.getPage(),
	first_page: 1,
	last_page: response.getTotalPages(),
	perPage: response.getPageSize(),
	total: response.getTotalCount()
});

/**
 * Custom hook for managing users data
 */
export const useUsers = () => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [usersData, setUsersData] = useState<Record<UserTabKey, PaginatedApiResponse>>({
		[USERS_TABS.LegalEase]: dummyPaginatedApiResponse,
		[USERS_TABS.Providers]: dummyPaginatedApiResponse
	});

	const toast = useClaimsToast();

	/**
	 * Fetches users data for a specific tab with filters
	 */
	const fetchUsers = async (tabKey: UserTabKey, filters: UserFilters): Promise<void> => {
		setIsLoading(true);

		try {
			// Map the filters to the getUsers API parameters
			const apiParams = {
				page: filters.page,
				pageSize: filters.pageSize,
				sortBy: filters.sortBy,
				sortOrder: filters.sortOrder,
				emailFilter: filters.emailFilter,
				nameFilter: filters.nameFilter,
				externalIdFilter: filters.externalIdFilter,
				rolesList: filters.rolesList,
				status: filters.status,
				userType: tabKey
			};
			const response: GetUsersResponse = await getUsers(apiParams);

			const returnedUsersData = response.getUsersList().map(mapUserToTableData);
			const returnedPaginationData = mapPaginationData(response);

			setUsersData((prev) => ({
				...prev,
				[tabKey]: {
					data: returnedUsersData,
					meta: returnedPaginationData
				}
			}));
		} catch (err) {
			toast("Error", (err as RpcError).message, "error");
		} finally {
			setIsLoading(false);
		}
	};

	/**
	 * Gets users data for a specific tab
	 */
	const getUsersData = (tabKey: UserTabKey): PaginatedApiResponse => {
		return usersData[tabKey];
	};

	return {
		isLoading,
		fetchUsers,
		getUsersData,
		setIsLoading
	};
};
